// WHERE WILL YOU POST IT? (owner 2026-10-23) The page's side of services/quicktools/canvas-shapes.js: the same table
// (canvas-shapes.json), the same choice of shapes for what the buyer picked, and the placement field's format.
import TABLE from './canvas-shapes.json';

export type ShapeKey = '9x16' | '4x5' | '1x1' | '3x4' | '16x9' | 'orig';
export type PlatformKey = keyof typeof TABLE.platforms;
export interface Place {
  mode: 'fill' | 'crop';
  pos: number;
}

export const SHAPE_KEYS = Object.keys(TABLE.shapes) as ShapeKey[];
/** The platforms in the order the page lists them; "Just for me" last. */
export const PLATFORM_KEYS = Object.keys(TABLE.platforms) as PlatformKey[];
export const PLATFORM_SHAPES = TABLE.platforms as Record<PlatformKey, ShapeKey[]>;
/** The shapes "Just for me" can pick freely. */
export const FREE_SHAPES = TABLE.free as ShapeKey[];
const TIERS = TABLE.tiers as Record<ShapeKey, string>;

/** The ratio a shape is, for its label ("9:16"); orig from the picture's own size. */
export function ratioLabel(key: ShapeKey, picture?: { w: number; h: number } | null): string {
  if (key !== 'orig') return key.replace('x', ':');
  if (!picture) return '';
  const r = picture.w / picture.h;
  // The picture's own shape, named by the nearest simple ratio when it is close to one.
  const near = [[1, 1], [4, 5], [5, 4], [3, 4], [4, 3], [2, 3], [3, 2], [9, 16], [16, 9]].find(([a, b]) => Math.abs(r - a / b) < 0.02);
  return near ? `${near[0]}:${near[1]}` : `${r.toFixed(2)}:1`;
}

/** A shape's output size: the table's, or the picture's own at the cover's size (short side 1080, long side at most 1920). */
export function sizeOf(key: ShapeKey, picture?: { w: number; h: number } | null): { w: number; h: number } {
  const fixed = (TABLE.shapes as Record<string, { w: number; h: number } | null>)[key];
  if (fixed) return fixed;
  if (!picture || !(picture.w > 0 && picture.h > 0)) return { w: 1080, h: 1920 };
  const even = (v: number) => Math.max(2, Math.round(v / 2) * 2);
  const { w: pw, h: ph } = picture;
  let w: number;
  let h: number;
  if (pw >= ph) {
    h = 1080;
    w = (pw / ph) * 1080;
    if (w > 1920) { w = 1920; h = (ph / pw) * 1920; }
  } else {
    w = 1080;
    h = (ph / pw) * 1080;
    if (h > 1920) { h = 1920; w = (pw / ph) * 1920; }
  }
  return { w: even(w), h: even(h) };
}

const allowed = (rule: string, tier: string | null) => rule === 'any' || rule === tier;

/**
 * The shapes made for what was picked, as the server will make them (canvas-shapes.js shapePlan, with the page's
 * formats=all): `shapes` (main first) drawn from the picture, `bonus` (what the option includes anyway, cut from the
 * 9:16 loop: square, 4x5, youtube, apple; tier: 'pack' for the Full pack, else anything), and `dropped`, picked but not
 * made (a 3:4 alone). Every shape asked for is made with every option (owner 2026-10-23, "Every option").
 */
export function shapePlan(platforms: string[], free: string[], tier: string | null, pictureOnly = false) {
  const asked: ShapeKey[] = [];
  const add = (k: string) => { if ((SHAPE_KEYS as string[]).includes(k) && !asked.includes(k as ShapeKey)) asked.push(k as ShapeKey); };
  const picked = platforms.filter((p) => (PLATFORM_KEYS as string[]).includes(p)) as PlatformKey[];
  const own = free.filter((k) => (SHAPE_KEYS as string[]).includes(k));
  picked.forEach((p) => PLATFORM_SHAPES[p].forEach(add));
  own.forEach(add);
  const appleOk = allowed(TABLE.appleTier, tier);
  let apple = picked.includes('apple');
  const shapes = asked.filter((k) => k !== '3x4' || apple || own.includes('3x4'));
  const dropped = asked.filter((k) => !shapes.includes(k));
  if ((!shapes.length || picked.includes('spotify') || (!picked.length && !own.length)) && !shapes.includes('9x16')) shapes.unshift('9x16');
  const main = shapes.includes('9x16') ? '9x16' : shapes[0];
  const inc: Record<string, ShapeKey> = { square: '1x1', '4x5': '4x5', youtube: '16x9' };
  // Just a moving picture gets only the shapes picked: nothing it didn't ask for (owner 2026-10-23).
  let bonus = pictureOnly ? [] : Object.keys(inc).filter((key) => allowed(TIERS[inc[key]], tier) && !shapes.includes(inc[key]));
  const appleBonus = !pictureOnly && appleOk && !apple;
  if (main === '9x16') {
    if (appleBonus) bonus.push('apple');
  } else {
    bonus.forEach((key) => shapes.push(inc[key]));
    if (appleBonus) {
      apple = true;
      (['1x1', '3x4'] as ShapeKey[]).forEach((k) => { if (!shapes.includes(k)) shapes.push(k); });
    }
    bonus = [];
  }
  return { main, shapes: [main, ...shapes.filter((k) => k !== main)] as ShapeKey[], bonus, dropped, apple };
}

/** How many platforms an artist ticked, the count the price follows (canvas-shapes.js platformCount; "Just for me" is none). */
export function platformCount(platforms: string[]): number {
  return new Set(platforms.filter((p) => p !== 'me' && (PLATFORM_KEYS as string[]).includes(p))).size;
}

/** The `place` field: "9x16:fill:0.87,16x9:crop:0.4" <-> { key: Place }. */
export function parsePlace(v: string): Partial<Record<ShapeKey, Place>> {
  const out: Partial<Record<ShapeKey, Place>> = {};
  for (const part of (v || '').split(',')) {
    const [key, mode, pos] = part.split(':');
    if (!(SHAPE_KEYS as string[]).includes(key) || (mode !== 'fill' && mode !== 'crop')) continue;
    const p = Number(pos);
    out[key as ShapeKey] = { mode, pos: Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : 0.5 };
  }
  return out;
}
export function formatPlace(places: Partial<Record<ShapeKey, Place>>): string {
  return Object.entries(places)
    .filter(([, p]) => p)
    .map(([k, p]) => `${k}:${p!.mode}:${p!.pos.toFixed(3)}`)
    .join(',');
}

/**
 * The picture cut to a shape at `pos` (the buyer's own window, mode 'crop'), as the server cuts it (extend.js
 * extendStill crop: cover-fit, `pos` along the side that overflows), at most `long` px on its long side. A data URL.
 */
export async function cropStill(src: string, size: { w: number; h: number }, pos: number, long = 800): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const k = long / Math.max(size.w, size.h);
  const W = Math.round(size.w * k);
  const H = Math.round(size.h * k);
  const c = Object.assign(document.createElement('canvas'), { width: W, height: H });
  const s = Math.max(W / img.naturalWidth, H / img.naturalHeight);
  const w = img.naturalWidth * s;
  const h = img.naturalHeight * s;
  const g = c.getContext('2d');
  if (g) {
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, -(w - W) * pos, -(h - H) * pos, w, h);
  }
  return c.toDataURL('image/jpeg', 0.86);
}
